import { format, isDate, isValid, parse, parseISO } from 'date-fns';
/* Http Status Checker  */
import FileSaver from 'file-saver';
import Fuse from 'fuse.js';
// eslint-disable-next-line no-restricted-imports -- pre-existing Immutable usage; migrate off when touched
import { Map } from 'immutable';
import _ from 'lodash';
import * as XLSX from 'xlsx';

export function capitalizeEachWordInString(value) {
  const words = value.split(' ').filter(v => {
    return v;
  });

  for (let i = 0; i < words.length; i++) {
    words[i] = words[i][0].toUpperCase() + words[i].substr(1);
  }

  return words.join(' ');
}

export function checkHttpStatus(response) {
  // console.log("checkhttp", response);
  if (response.status < 200 || response.status >= 400) {
    const error = new Error(response.status + ' ' + response.statusText);
    error.type = response.status;
    // this is a catch all for timeouts
    if (response.status === 502 || response.status === 504) {
      alert('Unexpected Network Timeout.  You may need to refresh the current page.');
    } else if (response.status === 500) {
      error.errors = { 500: 'Unexpected Network Error.  Please contact customer support' };
      throw error;
    }
    return response.json().then(body => {
      error.errors = body;
      throw error;
    });
    // console.log("RESPONSE", response, response.json());
  }

  return response;
}

/* Comparison Functions */
/**
 * Compares two semantic versions (semver)
 * @param {string} current Version to check if it is higher than
 * @param {string} compareTo version to compare to
 * @returns {boolean}
 */
export function versionCompare(current, compareTo) {
  if (current && compareTo) {
    const currentVersion = current.split();
    const compareToVersion = compareTo.split();
    for (let i = 0; i < currentVersion.length; ++i) {
      if (compareToVersion.length === i) {
        return 1;
      }

      if (currentVersion[i] === compareToVersion[i]) {
        continue;
      } else if (currentVersion[i] > compareToVersion[i]) {
        return 1;
      } else {
        return -1;
      }
    }

    if (currentVersion.length !== compareToVersion.length) {
      return -1;
    }

    return 0;
  }
  return 0;
}

export function compareAlphanum(a, b) {
  function chunkify(t) {
    const tz = [];
    let x = 0;
    let y = -1;
    let n = 0;
    let i;
    let j;

    while ((i = (j = t.charAt(x++)).charCodeAt(0))) {
      const m = i == 46 || (i >= 48 && i <= 57);
      if (m !== n) {
        tz[++y] = '';
        n = m;
      }
      tz[y] += j;
    }
    return tz;
  }

  const aa = chunkify(('' + a).toLowerCase());
  const bb = chunkify(('' + b).toLowerCase());

  for (let x = 0; aa[x] && bb[x]; x++) {
    if (aa[x] !== bb[x]) {
      const c = Number(aa[x]);
      const d = Number(bb[x]);
      if (c == aa[x] && d == bb[x]) {
        return c - d;
      }
      return aa[x] > bb[x] ? 1 : -1;
    }
  }
  return aa.length - bb.length;
}

// immutable comparison, .get('identifier') can be used to sort by name, description, or something along those lines.
export function compareStringAsc(identifier) {
  return (a, b) => {
    if (a.get(identifier) < b.get(identifier)) {
      return -1;
    }
    if (a.get(identifier) > b.get(identifier)) {
      return 1;
    }
    return 0;
  };
}

export function compareStringDesc(identifier) {
  return (a, b) => {
    if (a.get(identifier) < b.get(identifier)) {
      return 1;
    }
    if (a.get(identifier) > b.get(identifier)) {
      return -1;
    }
    return 0;
  };
}

export function statusToInt(status) {
  switch (status) {
    case 'good':
      return 1;
    case 'no radio':
      return 2;
    case 'unresolved maintenance':
      return 3;
    case 'offline':
      return 4;
    case 'bad':
      return 5;
    default:
      return 6;
  }
}

export function compareStatusAsc(a, b) {
  const c = statusToInt(a.get('status', -1));
  const d = statusToInt(b.get('status', -1));
  if (c < d) {
    return -1;
  }
  if (c > d) {
    return 1;
  }
  return 0;
}

export function compareStatusDesc(a, b) {
  return compareStatusAsc(a, b) * -1;
}

export function latitudeFormatter(lat) {
  if (lat === 0) return '0.0000\u00B0';
  const absLat = Math.abs(lat).toFixed(4);
  return lat < 0 ? `${absLat}\u00B0 S` : `${absLat}\u00B0 N`;
}

export function longitudeFormatter(long) {
  if (long === 0) return '0.0000\u00B0';
  const absLong = Math.abs(long).toFixed(4);
  return long < 0 ? `${absLong}\u00B0 W` : `${absLong}\u00B0 E`;
}

export function getExcelFile(list, columns, headers = [[]], title = null, wscols = null, footers = null) {
  // expect immutable list of data for now

  let newTitle = title;
  let newCols = wscols;

  function sheetFromArrayOfArrays(data) {
    const ws = {};
    const range = { s: { c: 10000000, r: 10000000 }, e: { c: 0, r: 0 } };
    for (let R = 0; R != data.length; ++R) {
      for (let C = 0; C != data[R].length; ++C) {
        if (range.s.r > R) {
          range.s.r = R;
        }
        if (range.s.c > C) {
          range.s.c = C;
        }
        if (range.e.r < R) {
          range.e.r = R;
        }
        if (range.e.c < C) {
          range.e.c = C;
        }
        const cell = { v: data[R][C] };
        if (cell.v == null) {
          continue;
        }
        const cellRef = XLSX.utils.encode_cell({ c: C, r: R });

        if (typeof cell.v === 'number') {
          cell.t = 'n';
        } else if (typeof cell.v === 'boolean') {
          cell.t = 'b';
        } else {
          cell.t = 's';
        }

        ws[cellRef] = cell;
      }
    }

    if (range.s.c < 10000000) {
      ws['!ref'] = XLSX.utils.encode_range(range);
    }
    return ws;
  }

  // get headers in
  const data = headers;
  // get data for each column
  list.forEach(obj => {
    const arr = [];
    columns.forEach(column => {
      if (typeof obj.get === 'function') {
        arr.push(obj.get(column, ''));
      } else {
        arr.push(_.get(obj, column, ''));
      }
    });
    data.push(arr);
  });

  if (footers) {
    footers.forEach(foot => data.push(foot));
  }

  /* data should be done */

  // reasonable wscols default if none are passed in
  if (newCols == null) {
    newCols = [];
    for (let ab = 0; ab < columns.length; ab++) {
      newCols.push({ wch: 30 });
    }
  }

  const wsName = 'Sheet 1';

  function Workbook() {
    if (!(this instanceof Workbook)) {
      return new Workbook();
    }
    this.SheetNames = [];
    this.Sheets = {};
  }

  const wb = new Workbook();
  const ws = sheetFromArrayOfArrays(data);

  ws['!cols'] = newCols;
  /* add worksheet to workbook */
  wb.SheetNames.push(wsName);
  wb.Sheets[wsName] = ws;
  const wbout = XLSX.write(wb, { bookType: 'xlsx', bookSST: true, type: 'binary' });

  function s2ab(s) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i != s.length; ++i) {
      view[i] = s.charCodeAt(i) & 0xff;
    }
    return buf;
  }

  if (newTitle == null) {
    newTitle = 'excel-' + Date.now();
  }
  FileSaver.saveAs(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), newTitle + '.xlsx');
}

export const basicSearch = (data, searchTerm, searchFields) => {
  // breakout if no search Term
  if (searchTerm == '') {
    return data;
  }

  const options = {
    tokenize: true,
    matchAllTokens: true,
    findAllMatches: true,
    threshold: 0.01,
    location: 0,
    distance: 10000,
    maxPatternLength: 32,
    keys: searchFields,
  };

  const fuse = new Fuse(data, options);
  return fuse.search(searchTerm);
};

// Reduce boilerplate a little by building action creators with this function.
export function makeActionCreator(type, ...argNames) {
  return function (...args) {
    const action = { type };
    argNames.forEach((arg, index) => {
      action[argNames[index]] = args[index];
    });
    return action;
  };
}

export const caseInsensitiveSort = (a, b) => {
  const a2 = (a + '').toLowerCase();
  const b2 = (b + '').toLowerCase();
  return a2 < b2 ? -1 : a2 > b2 ? 1 : 0;
};

export const firmwareVersionComparator = (a, b) => {
  const arrA = a.split('.');
  const arrB = b.split('.');

  let intA = _.toInteger(arrA[0]);
  let intB = _.toInteger(arrB[0]);

  // first number
  if (intA < intB) {
    return -1;
  }
  if (intA > intB) {
    return 1;
  }

  // middle number
  intA = _.toInteger(arrA[1]);
  intB = _.toInteger(arrB[1]);

  if (intA < intB) {
    return -1;
  }
  if (intA > intB) {
    return 1;
  }

  // last number
  intA = _.toInteger(arrA[2]);
  intB = _.toInteger(arrB[2]);

  if (intA < intB) {
    return -1;
  }
  if (intA > intB) {
    return 1;
  }
  return 0;
};

export const currencyRegex = /^[0-9]*[\\.]?[0-9]{0,2}$/; // matches 123123.23 only 2 decimals

export function luhnCheck(luhn) {
  let ca;
  let sum = 0;
  let mul = 0;
  let len = luhn.length;
  while (len--) {
    ca = parseInt(luhn.charAt(len), 10) << mul;
    sum += ca - (ca > 9) * 9; // sum += ca - (-(ca>9))|9
    // 1 <--> 0 toggle.
    mul ^= 1; // mul = 1 - mul;
  }
  return sum % 10 === 0 && sum > 0;
}

const knownICCIDprefixes = [
  '8901410325732', // AT&T
  '8901410325963', // AT&T
  '8901410325698', // AT&T
  '8901410325716', // AT&T
  '8901410325817', // AT&T
  '8901410325941', // AT&T
  '8901410325765', // AT&T
  '8901410327178', // new (2019-03-01) AT&T block
  '890126076136', // T-mobile
  '890126076137', // T-mobile
  '890126076138', // T-mobile
  '8931440400012', // International
  // '8901170425801', 2G
  // '890165090700',  2G
  // '890165090701',  2G
  // '8901170425800', 2G
  // '898821671000',  2G
  // '898821671050'   2G
];

const verizonPrefixes = ['0x00A100003B'];

export const suggestICCID = word => {
  // take word, slap it together with known to be length 18, 19, or 20
  let newWord = word;

  if (newWord.length < 7) {
    return [];
  }
  // if any letters are contained, assume it is a verizon
  if (/[a-zA-Z]+/.test(newWord)) {
    // verizon check slightly different, this makes sure it starts with the last letter of the prefix
    let front = '';
    if (newWord.length > 6) {
      front = newWord.slice(0, -6);
      newWord = newWord.slice(-6);
    }
    // word is always length 6 now.

    return verizonPrefixes.filter(x => x.toLowerCase().endsWith(front.toLowerCase())).map(x => x + newWord);
  }

  // do the standard ICCID check

  let front = '';
  if (newWord.length > 7) {
    front = newWord.slice(0, -7);
    newWord = newWord.slice(-7);
  }

  // word is always length 7 now.

  return knownICCIDprefixes
    .filter(x => x.endsWith(front))
    .map(x => x + newWord)
    .filter(x => luhnCheck(x));
};

export const formatIntegerWithCommas = x => {
  let n = x;
  if (typeof n === 'string') {
    try {
      n = parseFloat(n);
    } catch {
      n = 0.0;
    }
  }
  if (isNaN(n)) {
    n = 0.0;
  }
  return n
    .toFixed(0)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatNumberWithCommas = x => {
  let n = x;
  if (typeof n === 'string') {
    try {
      n = parseFloat(n);
    } catch {
      n = 0.0;
    }
  }
  if (isNaN(n)) {
    n = 0.0;
  }
  return n
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const sortHelper = (sort, sortDesc) =>
  sortDesc
    ? sort
        .split(',')
        .map(item => (item[0] === '-' ? item.slice(1) : '-' + item))
        .join(',')
    : sort;

export const dataTableParamFormatter = ({ limit, offset, search, sort, sortDesc, ...otherKeywords }) =>
  Map({
    limit,
    offset,
    text_search: search || '',
    o: sortHelper(sort, sortDesc),
    ...otherKeywords,
  });

export const mapToQueryParams = params =>
  params
    ? params
        .map((value, key) => encodeURIComponent(key) + '=' + encodeURIComponent(value))
        .toArray()
        .join('&')
    : '';

export const generateQueryParams = (
  { limit, offset, search, order, orderBy, dateRange, ...otherKeywords },
  options = {},
) => {
  const params = {};

  if (limit != null && limit > 0) {
    params.limit = limit;
  }

  if (offset != null && offset > 0) {
    params.offset = offset;
  }

  if (search) {
    params.text_search = search;
  }

  if (orderBy != null || orderBy != '') {
    params.o = sortHelper(orderBy, order === 'desc');
  }

  if (dateRange != null) {
    const { start, end, field } = dateRange;

    if (typeof field === 'object') {
      if (start) {
        if (isDate(start)) {
          params[`${field.start}`] = format(start, 'yyyy-MM-dd');
        } else {
          params[`${field.start}`] = format(parseISO(start), 'yyyy-MM-dd');
        }
      }

      if (end) {
        if (isDate(end)) {
          params[`${field.end}`] = format(end, 'yyyy-MM-dd');
        } else {
          params[`${field.end}`] = format(parseISO(end), 'yyyy-MM-dd');
        }
      }
    } else {
      if (start) {
        const parsedStart = parse(start, 'yyyy-MM-dd', new Date()); // Parse string to Date
        if (isValid(parsedStart)) {
          params[`${field}__gte`] = format(parsedStart, 'yyyy-MM-dd'); // Format correctly
        }
      }

      if (end) {
        const parsedEnd = parse(end, 'yyyy-MM-dd', new Date()); // Parse string to Date
        if (isValid(parsedEnd)) {
          params[`${field}__lte`] = format(parsedEnd, 'yyyy-MM-dd');
        }
      }
    }
  }

  Object.keys(otherKeywords).forEach(key => {
    params[key] = otherKeywords[key];
  });

  return params;
};

export const formatUserString = user =>
  user
    ? user.get('first_name', '') + (user.get('last_name', '') !== '' ? ' ' + user.get('last_name', '') : '')
    : 'Invalid User';

export const getQueryParam = (location, paramName) => {
  return new URLSearchParams(location.search).get(paramName);
};

export const getOrigins = placement => {
  switch (placement) {
    case 'left':
      return {
        anchorOrigin: {
          vertical: 'center',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'center',
          horizontal: 'right',
        },
      };
    case 'right':
      return {
        anchorOrigin: {
          vertical: 'center',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'center',
          horizontal: 'left',
        },
      };
    case 'top':
      return {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      };
    case 'bottom':
      return {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      };
    default:
      return {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      };
  }
};

export const getPasswordVerifier = (length = 8, uppercase = true, lowercase = true, numbers = true, symbols = true) => {
  return password => {
    const report = {
      isValid: true,
      metrics: {},
    };

    if (length) {
      const regExp = new RegExp(`(?=.{${length},})`);
      report.metrics.length = {
        description: 'Password length',
        value: length,
        status: regExp.test(password),
      };
    }

    if (uppercase) {
      const regExp = new RegExp(`(?=.*[A-Z])`);
      report.metrics.uppercase = {
        description: 'Contains uppercase letters',
        status: regExp.test(password),
      };
    }

    if (lowercase) {
      const regExp = new RegExp(`(?=.*[a-z])`);
      report.metrics.lowercase = {
        description: 'Contains lowercase letters',
        status: regExp.test(password),
      };
    }

    if (numbers) {
      const regExp = new RegExp(`(?=.*[0-9])`);
      report.metrics.numbers = {
        description: 'Contains numbers (0-9)',
        status: regExp.test(password),
      };
    }

    if (symbols) {
      const regExp = new RegExp(`(?=.*[!@#$%^&*])`);
      report.metrics.symbols = {
        description: 'Contains symbols (!@#$%^&*)',
        status: regExp.test(password),
      };
    }

    Object.values(report.metrics).forEach(metric => {
      if (!metric.status) {
        report.isValid = false;
      }
    });

    return report;
  };
};

export function testIpv4Address(value) {
  const reg =
    /^(2[0-1][0-9]|22[0-3]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return reg.test(value) || value.length === 0;
}
